import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, User } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/blog`);
        setPosts(res.data);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen pt-24" data-testid="blog-page">
      {/* Hero */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="blog-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-mint mb-6 opacity-0 animate-fade-up">
              Blog
            </p>
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-heading font-bold text-white tracking-tighter leading-[0.9] opacity-0 animate-fade-up animate-delay-100">
              Insights &
              <br />
              <span className="text-mint">perspectives.</span>
            </h1>
            <p className="mt-10 text-xl text-slate-400 leading-relaxed opacity-0 animate-fade-up animate-delay-200">
              Technical articles, industry analysis, and engineering best practices from our team.
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-16 md:py-24 bg-obsidian-light" data-testid="blog-posts">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white/5 border border-white/5 p-8">
                  <Skeleton className="h-4 w-24 bg-white/10 mb-6" />
                  <Skeleton className="h-8 w-full bg-white/10 mb-4" />
                  <Skeleton className="h-24 w-full bg-white/10" />
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-400 text-xl">No blog posts available yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post, idx) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className={`card-beam p-8 group ${idx === 0 ? 'md:col-span-2 lg:col-span-1' : ''}`}
                  data-testid={`blog-card-${post.slug}`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-xs font-mono uppercase tracking-wider px-3 py-1 bg-mint/10 text-mint border border-mint/20">
                      {post.category}
                    </span>
                  </div>
                  
                  <h2 className="text-xl font-heading font-bold text-white group-hover:text-mint transition-colors mb-4 line-clamp-2">
                    {post.title}
                  </h2>
                  
                  <p className="text-slate-400 leading-relaxed mb-8 line-clamp-3">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between pt-6 border-t border-white/5">
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <User className="w-4 h-4" />
                      <span>{post.author}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(post.created_at)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <NewsletterSection />
    </div>
  );
}

function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/newsletter`, { email });
      setSubscribed(true);
      setEmail("");
    } catch (error) {
      if (error.response?.status === 400) {
        setSubscribed(true);
      } else {
        console.error("Error subscribing:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-24 md:py-32 border-t border-white/5" data-testid="newsletter-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="glass-card p-12 md:p-20 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white tracking-tight">
            Stay informed
          </h2>
          <p className="mt-6 text-xl text-slate-400 max-w-xl mx-auto">
            Subscribe to receive updates on new articles, product announcements, and industry insights.
          </p>
          
          {subscribed ? (
            <div className="mt-10 text-mint text-lg" data-testid="newsletter-success">
              Thank you for subscribing.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-12 flex flex-col sm:flex-row justify-center gap-3 max-w-md mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 bg-white/5 border border-white/10 text-white placeholder:text-slate-600 px-4 py-4 focus:border-mint focus:outline-none transition-colors"
                data-testid="newsletter-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="bg-mint text-black px-6 py-4 font-bold text-sm tracking-wide uppercase hover:bg-white transition-colors disabled:opacity-50"
                data-testid="newsletter-submit-btn"
              >
                {loading ? "..." : "Subscribe"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
